/**
 * components/forms/SendFormModal.tsx
 *
 * Modal for sharing a form template via a shareable link.
 * Automatically creates a shared link on open if none exists.
 * Email sending is planned for a future release.
 *
 * Refactored Mar 2026: auto-generate link on open, removed toggle active,
 * removed redundant header, removed Fermer button, fixed French accents.
 * Updated May 2026: logEvent('request_sent') on copy link — single source of truth
 * for the "Tentatives d'envoi" KPI. Throttled to 1 event per 5 s per template to
 * prevent double-click spam. The copy action itself (clipboard + visual feedback)
 * is never throttled — only the analytics event is.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { Link2, Mail, Copy, Check, ExternalLink } from 'lucide-react';
import type { Database } from '../../types/database';
import { captureError } from '../../utils/errorReporting';
import { logEvent } from '../../utils/analytics';

type FormTemplate = Database['public']['Tables']['form_templates']['Row'];
type SharedLink = Database['public']['Tables']['shared_links']['Row'];

interface SendFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: FormTemplate;
  userId: string;
  onSuccess: () => void;
}

export default function SendFormModal({
  isOpen,
  onClose,
  template,
  userId,
  onSuccess,
}: SendFormModalProps) {
  const { showToast } = useToast();
  const [sharedLink, setSharedLink] = useState<SharedLink | null>(null);
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  // Tracks the last time a request_sent event was fired for this template instance.
  // Prevents counting rapid re-copies of the same link as separate send attempts.
  const lastSentLogAtRef = useRef<number>(0);

  const shareUrl = sharedLink
    ? `${window.location.origin}/f/${sharedLink.share_token}`
    : null;

  const fetchOrCreateLink = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const { data: existing, error: fetchError } = await supabase
        .from('shared_links')
        .select('*')
        .eq('template_id', template.id)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        setSharedLink(existing);
        return;
      }

      const { data: created, error: createError } = await supabase
        .from('shared_links')
        .insert({ template_id: template.id, user_id: userId })
        .select()
        .single();

      if (createError) throw createError;
      setSharedLink(created);
      onSuccess();
    } catch (err) {
      captureError(err, { component: 'SendFormModal', action: 'fetchOrCreateLink', templateId: template.id });
      showToast('Erreur lors de la création du lien', 'error');
    } finally {
      setLoading(false);
    }
  }, [isOpen, template.id, userId, onSuccess, showToast]);

  useEffect(() => {
    if (isOpen) {
      setLinkCopied(false);
      setSharedLink(null);
      lastSentLogAtRef.current = 0;
      fetchOrCreateLink();
    }
  }, [isOpen, fetchOrCreateLink]);

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy link:', err);
      showToast('Impossible de copier le lien', 'error');
      return;
    }
    try {
      const now = Date.now();
      if (now - lastSentLogAtRef.current >= 5_000) {
        lastSentLogAtRef.current = now;
        logEvent('request_sent', { method: 'copy_link', template_id: template.id, shared_link_present: Boolean(sharedLink) });
      }
    } catch (err) {
      console.error('Failed to log request_sent event:', err);
    }
  };

  const handleClose = () => {
    setSharedLink(null);
    setLinkCopied(false);
    lastSentLogAtRef.current = 0;
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Envoyer - ${template.name}`}
      size="md"
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-white text-neutral-900 shadow-sm border border-neutral-200"
          >
            <Link2 className="w-4 h-4" />
            Lien partageable
          </button>
          <button
            type="button"
            disabled
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm text-neutral-400 cursor-not-allowed relative"
          >
            <Mail className="w-4 h-4" />
            Envoyer par e-mail
            <span className="ml-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200 leading-none">
              Bientôt
            </span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner size="sm" />
          </div>
        ) : (
          <div className="bg-neutral-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={shareUrl ?? ''}
                readOnly
                className="flex-1 min-w-0 px-3 py-2 bg-white border border-neutral-200 rounded-xl text-sm text-neutral-700 font-mono truncate focus:outline-none"
              />
              <Button
                type="button"
                variant={linkCopied ? 'secondary' : 'primary'}
                onClick={handleCopyLink}
                disabled={!shareUrl}
                className="flex-shrink-0"
              >
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-1.5" />
                    Copié
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1.5" />
                    Copier
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-neutral-500 leading-relaxed">
              Partagez ce lien avec vos clients.
            </p>

            <Button
              type="button"
              variant="secondary"
              onClick={() => { if (shareUrl) window.open(shareUrl, '_blank'); }}
              disabled={!shareUrl}
              className="w-full"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" />
              Ouvrir le lien
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
